import { useRouter } from 'next/router';
import { FC, useEffect, useMemo } from 'react';

import { Optional } from '@common/type/CommonTypes';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, DialogActions, DialogTitle, Rating, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import RatingStarIcon from '../ratingStarIcon';

import { TextArea } from '@components/atoms/textArea';
import BaseModalWrapper from '@components/share/modal/baseModalWrapper';
import { useBreakpointV2 } from '@helpers/use';
import { numberWithCommas } from '@helpers/utils';
import { useCourseReview } from '@hooks/course/courseReview/useCourseReview';
import { CreateOrUpdateCourseReviewPayload } from '@hooks/course/courseReview/useCourseReviewCreateOrUpdate';

const MIN_RATING = 1;
const MAX_RATING = 5;
const MAX_COMMENT_LENGTH = 1000;

type CreateOrUpdateCourseReviewDialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

const CreateOrUpdateCourseReviewDialog: FC<CreateOrUpdateCourseReviewDialogProps> = ({ isOpen, setIsOpen }) => {
  const { t } = useTranslation('store_course');
  const theme = useTheme();
  const { isMobile } = useBreakpointV2();

  const router = useRouter();
  const idName = router.query.idName as Optional<string>;

  const COMMENT_MIN_ROWS = isMobile ? 10 : 8;

  const {
    handleCreateOrUpdateCourseReview,
    isCreateOrUpdateCourseReviewDataLoading,
    courseReviewMeData,
  } = useCourseReview({
    idName,
  });

  const createOrUpdateCourseReviewSchemaValidate = yup.object({
    rating: yup
      .number()
      .min(MIN_RATING, t('course_detail.course_review.please_review_rating_message'))
      .required(),
    comment: yup.string().test('validate length', 'error', (value) => (value || '').length <= MAX_COMMENT_LENGTH),
  });

  const createOrUpdateCourseReviewForm = useForm<CreateOrUpdateCourseReviewPayload>({
    mode: 'onChange',
    defaultValues: {
      rating: 0,
    },
    resolver: yupResolver(createOrUpdateCourseReviewSchemaValidate),
  });

  const {
    control,
    formState: { errors },
    watch,
    setValue,
    reset,
    handleSubmit,
  } = createOrUpdateCourseReviewForm;

  const comment = watch('comment');

  const isDisabledSubmit = useMemo(() => {
    return isCreateOrUpdateCourseReviewDataLoading || !!errors?.rating || !!errors?.comment;
  }, [isCreateOrUpdateCourseReviewDataLoading, errors?.rating, errors?.comment]);

  useEffect(() => {
    if (isOpen) {
      if (courseReviewMeData) {
        setValue('rating', courseReviewMeData.rating);
        setValue('comment', courseReviewMeData.comment);
      }
    } else {
      reset();
    }
  }, [isOpen, courseReviewMeData, setValue, reset]);

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <BaseModalWrapper
      open={isOpen}
      onClose={handleClose}
      maxWidth={750}
      paperStyle={{ width: 750, py: 0, maxHeight: { xs: 'calc(100vh - 48px)', md: 'calc(100vh - 96px)' } }}
      boxStyle={{ maxHeight: { xs: 'calc(100vh - 48px)', md: 'calc(100vh - 96px)' }, overflow: 'hidden' }}
      keepMounted={false}
      disabledBackDrop
    >
      <Box
        sx={{
          maxHeight: 'inherit',
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 1, md: 3 },
          py: { xs: 3, md: 5 },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 0, pb: 1 }}>
          <Typography variant="h4">{t('course_detail.course_review.rate_review')}</Typography>
        </DialogTitle>

        <Box textAlign="center">
          <Typography component="p" variant="body1lanna" color={theme.palette.textV2.secondary} pb={1}>
            {t('course_detail.course_review.click_to_review')}
          </Typography>
          <Controller
            name="rating"
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                <Rating
                  value={value}
                  max={MAX_RATING}
                  onChange={onChange}
                  icon={<RatingStarIcon rootSx={{ width: 30, height: 30 }} iconStyle={{ fontSize: 20 }} />}
                  emptyIcon={<RatingStarIcon isEmpty rootSx={{ width: 30, height: 30 }} iconStyle={{ fontSize: 20 }} />}
                  size="small"
                />
                {errors?.rating ? (
                  <Box mt={1}>
                    <Typography variant="BaseLegacy" color={theme.palette.errorV2.main}>
                      {errors.rating.message}
                    </Typography>
                  </Box>
                ) : null}
              </>
            )}
          />
        </Box>

        <Controller
          name="comment"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextArea
              value={value}
              onChange={onChange}
              placeholder={t('course_detail.course_review.create_or_update_review.comment.placeholder')}
              minRows={COMMENT_MIN_ROWS}
              inputProps={{ style: { padding: '8px 16px' } }}
              inputPropsStyle={{ style: { fontSize: 18, lineHeight: '22px', padding: 0 } }}
              sx={{ overflowY: 'auto', mt: 2, py: '1px' }}
            />
          )}
        />
        <Box display="flex" justifyContent="end" gap="2px" mb={{ xs: 2, md: 3 }} color={theme.palette.textV2.secondary}>
          <Typography variant="helperText" color={errors.comment?.message ? theme.palette.errorV2.main : ''}>
            {numberWithCommas((comment || '').length)}
          </Typography>
          /<Typography variant="helperText">{numberWithCommas(MAX_COMMENT_LENGTH)}</Typography>
        </Box>

        <DialogActions
          disableSpacing
          sx={{
            p: '8px 0 0',
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            gap: { xs: 2, md: 3 },
          }}
        >
          <Button variant="outlined" fullWidth onClick={handleClose}>
            {t('course_detail.course_review.skip')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disableElevation
            disabled={isDisabledSubmit}
            onClick={handleSubmit(handleCreateOrUpdateCourseReview)}
          >
            {t('course_detail.course_review.enter')}
          </Button>
        </DialogActions>
      </Box>
    </BaseModalWrapper>
  );
};

export default CreateOrUpdateCourseReviewDialog;
